import React from 'react';
import { Route, Redirect, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { Layout } from './components/Layout';
import { LiveTimetable } from './components/LiveTimetable';
import { CompetitionAdmin, EntryAdmin } from './components/CompetitionAdmin';
import { Draw } from './components/Draw';
import { DrawExportPdf } from './components/DrawExportPdf';
import { Commentary } from './components/Commentary';
import { EventsAdmin } from './components/event-admin';
import RaceCards from './components/RaceCards';
import { Racing } from './components/racing';
import { RegistrationDesk } from './components/RegistrationDesk';
import { ClubEntrySummary } from './components/ClubEntrySummary';
import { SystemSettings } from './components/SystemSettings';
import { BandingHelper } from './components/BandingHelper';
import { VictorLudorumAdmin } from './components/VictorLudorumAdmin';
import { MeetingSelection } from './components/MeetingSelection';
import { AuthProvider } from './components/auth/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginPage } from './components/auth/LoginPage';
import { MultiStepSignupPage } from './components/auth/MultiStepSignupPage';
import { ForgotPasswordPage } from './components/auth/ForgotPasswordPage';
import { OAuthCallbackPage } from './components/auth/OAuthCallbackPage';
import { ResetPasswordPage } from './components/auth/ResetPasswordPage';
import { CompetitionContext } from './components/CompetitionContext';
import { useAuth } from './components/auth/AuthContext';
import { RegattaUnderConstruction } from './components/RegattaUnderConstruction';
import { LandingPage } from './components/LandingPage';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { CookieConsentBanner, hasAnalyticsConsent } from './components/CookieConsentBanner';
import { supabase } from './supabaseClient';

import './css/custom.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const MEETING_SCOPED_BASE_PATHS = new Set([
    'live-timetable', 'draw', 'draw-export-pdf', 'racecards', 'race-cards',
    'competition-admin', 'entry-admin', 'events-admin', 'banding-helper', 'victor-ludorum',
    'system-settings', 'registration-desk', 'racing-dashboard',
    'commentary', 'club-summary'
]);

function getMeetingContextState(pathname, search) {
    const params = new URLSearchParams(search || '');
    const parsedMeetingId = Number.parseInt(String(params.get('meetingId') || '').trim(), 10);
    const hasMeetingId = Number.isFinite(parsedMeetingId) && parsedMeetingId > 0;

    const segments = String(pathname || '').split('/').filter(Boolean);
    const isCanonicalMeetingScoped = segments.length === 1 && MEETING_SCOPED_BASE_PATHS.has(segments[0]);
    const isFriendlyMeetingScoped = segments.length >= 2 && MEETING_SCOPED_BASE_PATHS.has(segments[1]);
    const requiresMeetingContext = isCanonicalMeetingScoped || isFriendlyMeetingScoped;

    return {
        hasMeetingContext: hasMeetingId || isFriendlyMeetingScoped,
        requiresMeetingContext
    };
}

function PublicRegattaRoute({ path, component: Component, ...rest }) {
    const { publicPagesLocked } = React.useContext(CompetitionContext);
    const { user, roles, loading, rolesLoading } = useAuth();

    if (loading || (user && rolesLoading)) return null;

    const hasAnyRole = Array.isArray(roles) && roles.length > 0;

    return (
        <Route
            path={path}
            {...rest}
            render={(props) => (
                publicPagesLocked && !hasAnyRole
                    ? <RegattaUnderConstruction />
                    : <Component {...props} />
            )}
        />
    );
}

const App = () => {
    const location = useLocation();
    const [analyticsConsent, setAnalyticsConsent] = React.useState(() => hasAnalyticsConsent());

    React.useEffect(() => {
        const onConsentUpdated = () => {
            setAnalyticsConsent(hasAnalyticsConsent());
        };

        window.addEventListener('cookie-consent-updated', onConsentUpdated);
        return () => window.removeEventListener('cookie-consent-updated', onConsentUpdated);
    }, []);

    // Initialise GA once and track page changes
    React.useEffect(() => {
        const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
        if (!measurementId || !analyticsConsent) return;
        ReactGA.initialize(measurementId);
    }, [analyticsConsent]);

    React.useEffect(() => {
        const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
        if (!measurementId || !analyticsConsent) return;
        ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    }, [location, analyticsConsent]);

    const [competition, setCompetition] = React.useState({
        competitionName: '',
        currentSettingsId: null,
        currentYear: null,
        ReDrawAvailable: false,
        RunDrawAvailable: false,
        publicPagesLocked: false,
        refreshCompetition: async () => {}
    });

    const fetchCompetition = React.useCallback(async () => {
        try {
            const { data } = await supabase.auth.getSession();
            const token = data?.session?.access_token || null;

            // Extract meeting context from the current URL
            const currentPath = window.location.pathname || '';
            const currentSearch = window.location.search || '';
            const segments = currentPath.split('/').filter(Boolean);
            const activeSlug = (segments.length >= 2 && MEETING_SCOPED_BASE_PATHS.has(segments[1]))
                ? segments[0] : '';
            const params = new URLSearchParams(currentSearch);
            const urlMeetingId = params.get('meetingId') || '';

            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            if (activeSlug) headers['x-meeting-slug'] = activeSlug;
            else if (urlMeetingId) headers['x-meeting-id'] = urlMeetingId;

            const response = await fetch('/api/meeting/current', { headers });
            if (response.status === 403) {
                const body = await response.json().catch(() => ({}));
                if (body?.code === 'REGATTA_LOCKED') {
                    setCompetition(prev => ({ ...prev, publicPagesLocked: true }));
                }
                return;
            }
            if (!response.ok) return;

            const actualData = await response.json();
            const meetingDate = String(actualData.meetingDate || '').trim();
            const parts = meetingDate.split('/');
            const yearFromDate = parts.length === 3 ? Number(parts[2]) : Number(actualData.currentYear || 0);
            const year = Number.isFinite(yearFromDate) && yearFromDate > 0 ? yearFromDate : null;
            const competitionName = year
                ? `${actualData.competitionName || ''} ${year}`.trim()
                : (actualData.competitionName || '');

            setCompetition(prev => ({
                ...prev,
                competitionName,
                currentSettingsId: actualData.currentSettingsId ?? null,
                currentYear: year,
                ReDrawAvailable: actualData.reDrawAvailable,
                RunDrawAvailable: actualData.runDrawAvailable,
                publicPagesLocked: Boolean(actualData.publicPagesLocked)
            }));
        } catch (error) {
            console.error('Failed to load current meeting settings:', error);
        }
    }, []);

    const meetingContextState = React.useMemo(
        () => getMeetingContextState(location.pathname, location.search),
        [location.pathname, location.search]
    );

    React.useEffect(() => {
        if (meetingContextState.requiresMeetingContext && !meetingContextState.hasMeetingContext) {
            setCompetition(prev => ({
                ...prev,
                competitionName: '',
                currentSettingsId: null,
                currentYear: null,
                ReDrawAvailable: false,
                RunDrawAvailable: false,
                publicPagesLocked: false
            }));
            return;
        }
        fetchCompetition();
    }, [fetchCompetition, meetingContextState]);

    const competitionValue = React.useMemo(() => ({
        ...competition,
        refreshCompetition: fetchCompetition
    }), [competition, fetchCompetition]);

    return (
        <AuthProvider>
            <CompetitionContext.Provider value={competitionValue}>
                <Layout>
                    <Route exact path="/" component={LandingPage} />
                    <Route exact path="/privacy-policy" component={PrivacyPolicy} />
                    <Route path="/auth/callback" component={OAuthCallbackPage} />
                    <Route path="/login" component={LoginPage} />
                    <Route path="/forgot-password" component={ForgotPasswordPage} />
                    <Route path="/reset-password" component={ResetPasswordPage} />
                    <Route path="/signup" component={MultiStepSignupPage} />
                    <ProtectedRoute path="/select-meeting" component={MeetingSelection} />
                    <PublicRegattaRoute path={['/live-timetable', '/:slug/live-timetable']} component={LiveTimetable} />
                    <PublicRegattaRoute path={['/draw', '/:slug/draw']} component={Draw} />
                    <ProtectedRoute path={['/draw-export-pdf', '/:slug/draw-export-pdf']} component={DrawExportPdf} requireAdmin />
                    <Route exact path="/race-cards">
                        <Redirect to="/racecards" />
                    </Route>
                    <ProtectedRoute path={['/events-admin', '/:slug/events-admin']} component={EventsAdmin} requireAnyRole={['Admin', 'SponsorAdmin']} />
                    <ProtectedRoute path={['/commentary', '/:slug/commentary']} component={Commentary} requireAnyRole={['Admin', 'Commentary', 'Registration']} />
                    <ProtectedRoute path={['/racecards', '/:slug/racecards', '/:slug/race-cards']} component={RaceCards} requireAnyRole={['Admin', 'Registration']} />
                    <ProtectedRoute path={['/competition-admin', '/:slug/competition-admin']} component={CompetitionAdmin} requireAdmin />
                    <ProtectedRoute path={['/entry-admin', '/:slug/entry-admin']} component={EntryAdmin} requireAdmin />
                    <ProtectedRoute path={['/banding-helper', '/:slug/banding-helper']} component={BandingHelper} requireAdmin />
                    <ProtectedRoute path={['/victor-ludorum', '/:slug/victor-ludorum']} component={VictorLudorumAdmin} requireAdmin />
                    <ProtectedRoute path={['/system-settings', '/:slug/system-settings']} component={SystemSettings} requireAdmin />
                    <ProtectedRoute path={['/registration-desk', '/:slug/registration-desk']} component={RegistrationDesk} requireAnyRole={['Admin', 'Registration']} />
                    <ProtectedRoute path={['/club-summary', '/:slug/club-summary']} component={ClubEntrySummary} requireAnyRole={['Admin', 'Registration']} />
                    <ProtectedRoute path={['/racing-dashboard', '/:slug/racing-dashboard']} component={Racing} requireAnyRole={['Admin', 'Registration']} />
                    <CookieConsentBanner />
                </Layout>
            </CompetitionContext.Provider>
        </AuthProvider>
    );
};

export default App;
